import { useState, useEffect, useRef } from 'react';
import { createPortal } from "react-dom";
import { useParams, useNavigate } from 'react-router-dom';
import { socket } from '../socket';
import { Users, Crown, Play, ArrowLeft, Settings, MessageSquare, Home, Gavel, FileText, ChevronRight, LogOut, Maximize, Share2 } from 'lucide-react';
import Board from '../components/Board';
import { TokenIcon } from '../components/TokenIcon';
import Dice from '../components/Dice';
import SettingsModal from '../components/SettingsModal';
import propertiesData from '../data/properties.json';
import { playStepSound, playDiceSound, playCashSound, playSuccessSound, playJailSound, playHonkSound, playChatSound, playVapurDudugu, playIstasyonZili, playTaxPaySound, playCardDrawSound, playHotelBuiltSound, playBigWinSound, playTickSound, playDoubleSalarySound, playTurnAlertSound, setSoundEnabled, playMetroSound, playElectricSound, playWaterSound, playCigdemSound } from '../utils/audio';
import './Game.css';
import { gameBrand } from '../config/brand';
import { BrandLogo } from '../components/BrandLogo';
import { useLanguage } from '../i18n/LanguageContext';
import TaxScenarioModal from '../components/TaxScenarioModal';

const CustomDropdown = ({ label, value, options, onChange, disabled, tooltip }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => String(opt.value) === String(value)) || options[0];

  return (
    <div className="custom-dropdown-container" ref={dropdownRef}>
      <label className="tooltip-container">
        {label}
        {tooltip && (
          <>
            <span className="tooltip-icon">?</span>
            <span className="tooltip-text">{tooltip}</span>
          </>
        )}
      </label>
      <div 
        className={`custom-dropdown-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'active' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span>{selectedOption.label}</span>
        {!disabled && <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>}
      </div>
      {isOpen && (
        <div className="custom-dropdown-menu">
          {options.map((opt) => (
            <div 
              key={opt.value}
              className={`custom-dropdown-item ${String(opt.value) === String(value) ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              <div className="item-label">{opt.label}</div>
              {opt.desc && <div className="item-desc">{opt.desc}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const cleanLogMessage = (msg) => {
  if (!msg) return '';
  return msg.replace(/\s*\[cmd:[^\]]+\]/g, '');
};

export default function Game() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { lang, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    if (roomId) {
      localStorage.setItem('lastActiveRoomId', roomId);
    }
  }, [roomId]);
  const [room, setRoom] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [lastDice, setLastDice] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [auctionBid, setAuctionBid] = useState('');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [tempName, setTempName] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [autoRoll, setAutoRoll] = useState(() => localStorage.getItem('pref_autoRoll') === 'true');
  const [autoBuy, setAutoBuy] = useState(() => localStorage.getItem('pref_autoBuy') === 'true');
  const [autoResolveCards, setAutoResolveCards] = useState(() => localStorage.getItem('pref_autoResolveCards') === 'true');
  const [showOthersCards, setShowOthersCards] = useState(() => localStorage.getItem('pref_showOthersCards') !== 'false');
  const [soundFx, setSoundFx] = useState(() => {
    const saved = localStorage.getItem('pref_soundFx') !== 'false';
    setSoundEnabled(saved);
    return saved;
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isPlayersModalOpen, setIsPlayersModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [latestLog, setLatestLog] = useState('');
  const [logVisible, setLogVisible] = useState(false);
  const [isPortraitMobile, setIsPortraitMobile] = useState(false);
  const [copiedRoom, setCopiedRoom] = useState(false);

  useEffect(() => {
    const checkOrientation = () => {
      setIsPortraitMobile(window.innerWidth <= 900 && window.innerHeight > window.innerWidth);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  let myPlayerId = localStorage.getItem('playerId');
  if (myPlayerId === 'undefined' || myPlayerId === 'null') {
    localStorage.removeItem('playerId');
    myPlayerId = null;
  }

  const isPlaying = room ? room.state === 'PLAYING' : false;
  const activePlayer = gameData?.players?.find(p => p.playerId === gameData?.activePlayerId);
  const me = gameData?.players?.find(p => p.playerId === myPlayerId);
  const isMyTurn = gameData?.activePlayerId === myPlayerId;
  const isAuction = gameData?.phase === 'AUCTION' && gameData?.auction;
  const isMyBidTurn = isAuction && gameData?.auction?.activeBidders?.[gameData?.auction?.currentBidderIndex] === myPlayerId;
  const isMyDebt = gameData?.phase === 'DEBT_RESOLUTION' && gameData?.debt && gameData?.debt?.debtorId === myPlayerId;

  // Auto-request landscape lock when active board game starts
  useEffect(() => {
    if (isPlaying && gameData && gameData.phase !== 'LOBBY') {
      try {
        if (window.screen?.orientation?.lock) {
          window.screen.orientation.lock('landscape').catch(() => {});
        }
      } catch (e) {}
    }
  }, [isPlaying, gameData?.phase]);

  // Screen Wake Lock API - Keep screen active/awake while game is playing (prevent mobile sleep)
  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      if ('wakeLock' in navigator && isPlaying) {
        try {
          wakeLock = await navigator.wakeLock.request('screen');
        } catch (err) {
          // Ignore wake lock error on restricted browsers
        }
      }
    };

    const handleUserInteraction = () => {
      if (!wakeLock && isPlaying) {
        requestWakeLock();
      }
    };

    if (isPlaying) {
      requestWakeLock();
      window.addEventListener('touchstart', handleUserInteraction, { passive: true });
      window.addEventListener('click', handleUserInteraction, { passive: true });
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isPlaying) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('touchstart', handleUserInteraction);
      window.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLock) {
        wakeLock.release().catch(() => {});
      }
    };
  }, [isPlaying]);

  const toggleFullscreen = () => {
    const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    } else {
      const elem = document.documentElement;
      if (!isIos && elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {
          triggerIosScrollFallback();
        });
      } else if (!isIos && elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else {
        triggerIosScrollFallback();
      }
    }
  };

  const triggerIosScrollFallback = () => {
    window.scrollTo(0, 1);
    showToast('iPhone (iOS) kısıtlaması nedeniyle web sitelerinde tam ekran API\'si engellenmiştir. URL çubuğunu tamamen gizleyip uygulama gibi oynamak için Chrome/Safari menüsünden "Ana Ekrana Ekle" (Add to Home Screen) seçeneğini kullanabilirsiniz.', 'info');
  };

  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedRoom(true);
      showToast('Link panoya kopyalandı!', 'success');
      setTimeout(() => setCopiedRoom(false), 2500);
    } catch (err) {
      showToast(`Oda linki: ${text}`, 'info');
    }
  };

  const handleShareRoom = () => {
    const roomUrl = `${window.location.origin}/room/${roomId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(roomUrl).then(() => {
        setCopiedRoom(true);
        showToast('Link panoya kopyalandı!', 'success');
        setTimeout(() => setCopiedRoom(false), 2500);
      }).catch(() => {
        fallbackCopyTextToClipboard(roomUrl);
      });
    } else {
      fallbackCopyTextToClipboard(roomUrl);
    }
  };

  useEffect(() => {
    if (gameData?.logs && gameData.logs.length > 0) {
      const systemLogs = gameData.logs.filter(l => !l.startsWith('💬'));
      if (systemLogs.length > 0) {
        const last = systemLogs[systemLogs.length - 1];
        setLatestLog(last);
        setLogVisible(true);
        
        const timer = setTimeout(() => {
          setLogVisible(false);
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [gameData?.logs]);

  const showToast = (msg, type = 'error') => {
    setToastMessage({ msg, type });
    setTimeout(() => {
      setToastMessage(prev => (prev && prev.msg === msg ? null : prev));
    }, 3500);
  };

  const handleToggleAutoRoll = (val) => {
    setAutoRoll(val);
    localStorage.setItem('pref_autoRoll', val ? 'true' : 'false');
  };
  const handleToggleAutoBuy = (val) => {
    setAutoBuy(val);
    localStorage.setItem('pref_autoBuy', val ? 'true' : 'false');
  };
  const handleToggleAutoResolveCards = (val) => {
    setAutoResolveCards(val);
    localStorage.setItem('pref_autoResolveCards', val ? 'true' : 'false');
  };
  const handleToggleShowOthersCards = (val) => {
    setShowOthersCards(val);
    localStorage.setItem('pref_showOthersCards', val ? 'true' : 'false');
  };
  const handleToggleSoundFx = (val) => {
    setSoundFx(val);
    localStorage.setItem('pref_soundFx', val ? 'true' : 'false');
    setSoundEnabled(val);
  };

  const tokenNamesMap = {
    'Vapur': 'Vapur',
    'SaatKulesi': 'Saat Kulesi',
    'Marti': 'Martı',
    'Boyoz': 'Boyoz',
    'NazarBoncugu': 'Nazar Boncuğu',
    'Palmiye': 'Palmiye',
    'KirmiziAraba': 'Kırmızı Araba',
    'Gevrek': 'Gevrek'
  };

  const lastBeepedSecondRef = useRef(-1);

  let localReconnectToken = localStorage.getItem('reconnectToken');
  if (localReconnectToken === 'undefined' || localReconnectToken === 'null') {
    localStorage.removeItem('reconnectToken');
    localReconnectToken = null;
  }

  useEffect(() => {
    if (!gameData?.turnTimerExpiresAt) {
      setTimeLeft(null);
      return;
    }
    lastBeepedSecondRef.current = -1;
    const interval = setInterval(() => {
      const rem = Math.max(0, Math.round((gameData.turnTimerExpiresAt - Date.now()) / 1000));
      setTimeLeft(rem);
      
      if (rem <= 10 && rem > 0 && rem !== lastBeepedSecondRef.current) {
        lastBeepedSecondRef.current = rem;
        playTickSound();
      }

      if (rem <= 0) {
        clearInterval(interval);
      }
    }, 250);
    
    const initialRem = gameData?.turnTimerExpiresAt ? Math.max(0, Math.round((gameData.turnTimerExpiresAt - Date.now()) / 1000)) : 0;
    setTimeLeft(initialRem);
    
    return () => clearInterval(interval);
  }, [gameData?.turnTimerExpiresAt, myPlayerId]);

  const prevActivePlayerIdRef = useRef(null);

  useEffect(() => {
    if (isPlaying && gameData?.activePlayerId) {
      if (prevActivePlayerIdRef.current !== gameData.activePlayerId) {
        prevActivePlayerIdRef.current = gameData.activePlayerId;
        playTurnAlertSound();
      }
    } else {
      prevActivePlayerIdRef.current = null;
    }
  }, [gameData?.activePlayerId, isPlaying]);

  const gameDataRef = useRef(null);

  // Visual detail overlay & reveal states
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [activeHonks, setActiveHonks] = useState({});
  const [honkCooldown, setHonkCooldown] = useState(false);
  const [showQuickChat, setShowQuickChat] = useState(false);
  const [quickChatCooldown, setQuickChatCooldown] = useState(false);
  const [activeSidebarTab, setActiveSidebarTab] = useState('chat');
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [completionAlert, setCompletionAlert] = useState(null);
  const [taxAlert, setTaxAlert] = useState(null);
  const [isLobbyChatOpen, setIsLobbyChatOpen] = useState(false);
  const lobbyChatEndRef = useRef(null);
  const lobbyChatContainerRef = useRef(null);

  useEffect(() => {
    if (lobbyChatContainerRef.current) {
      lobbyChatContainerRef.current.scrollTop = lobbyChatContainerRef.current.scrollHeight;
    }
  }, [room?.chatLogs]);

  const triggerConfetti = () => {
    const canvas = document.getElementById('confetti-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    let particles = [];
    const colors = ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#a855f7', '#ef4444'];
    
    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        r: Math.random() * 6 + 4,
        d: Math.random() * canvas.height,
        color: colors[Math.floor(Math.random() * colors.length)],
        tilt: Math.random() * 10 - 5,
        tiltAngleIncremental: Math.random() * 0.07 + 0.02,
        tiltAngle: 0
      });
    }
    
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let active = false;
      
      particles.forEach((p) => {
        p.tiltAngle += p.tiltAngleIncremental;
        p.y += (Math.cos(p.d) + 3 + p.r / 2) / 2;
        p.x += Math.sin(p.tiltAngle);
        p.tilt = Math.sin(p.tiltAngle - p.r / 2) * 5;
        
        if (p.y < canvas.height) {
          active = true;
        }
        
        ctx.beginPath();
        ctx.lineWidth = p.r;
        ctx.strokeStyle = p.color;
        ctx.moveTo(p.x + p.tilt + p.r / 2, p.y);
        ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 2);
        ctx.stroke();
      });
      
      if (active) {
        requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
    
    draw();
    try {
      playBigWinSound();
    } catch(e) {}
  };

  const quickMessages = [
    "Tapuyu ver.",
    "Kira vakti.",
    "Hadi be!",
    "Kordon benim.",
    "Açık artırmada görüşürüz.",
    "O parayla Çeşme zor.",
    "Çiğdemini çitle, sıranı bekle.",
    "Honkkk!",
    "Karakol sana yakıştı.",
    "Semti kapattım."
  ];

  const handleHonkClick = () => {
    if (honkCooldown) return;
    sendCommand('HONK');
    setHonkCooldown(true);
    setTimeout(() => setHonkCooldown(false), 2000);
  };

  const handleSendQuickMessage = (msg) => {
    if (quickChatCooldown) return;
    socket.emit('sendChatMessage', { roomId, message: msg });
    setQuickChatCooldown(true);
    setTimeout(() => setQuickChatCooldown(false), 1500);
    setShowQuickChat(false);
  };

  const logsLengthRef = useRef(0);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const tickerEndRef = useRef(null);
  const tickerContainerRef = useRef(null);

  useEffect(() => {
    if (gameData?.logs) {
      if (logsLengthRef.current > 0 && gameData.logs.length > logsLengthRef.current) {
        const newLogs = gameData.logs.slice(logsLengthRef.current);
        const newChats = newLogs.filter(l => l.startsWith('💬'));
        if (newChats.length > 0) {
          playChatSound();
          if (activeSidebarTab !== 'chat') {
            setUnreadChatCount(prev => prev + newChats.length);
          }
        }
      }
      logsLengthRef.current = gameData.logs.length;
    }
  }, [gameData?.logs, activeSidebarTab]);

  // Auto-scroll chat & announcements (ticker) whenever new logs arrive, tab switches, or drawer opens
  useEffect(() => {
    const scrollToBottom = () => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
      if (chatEndRef.current) {
        chatEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    };
    scrollToBottom();
    const t1 = setTimeout(scrollToBottom, 50);
    const t2 = setTimeout(scrollToBottom, 150);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [gameData?.logs, activeSidebarTab, isChatOpen]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (tickerContainerRef.current) {
        tickerContainerRef.current.scrollTop = tickerContainerRef.current.scrollHeight;
      }
      if (tickerEndRef.current) {
        tickerEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    };
    if (activeSidebarTab === 'ticker') {
      scrollToBottom();
      const t1 = setTimeout(scrollToBottom, 50);
      const t2 = setTimeout(scrollToBottom, 150);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [gameData?.logs, activeSidebarTab, isChatOpen]);

  useEffect(() => {
    const scrollToBottom = () => {
      if (lobbyChatContainerRef.current) {
        lobbyChatContainerRef.current.scrollTop = lobbyChatContainerRef.current.scrollHeight;
      }
      if (lobbyChatEndRef.current) {
        lobbyChatEndRef.current.scrollIntoView({ behavior: 'auto' });
      }
    };
    scrollToBottom();
    const t1 = setTimeout(scrollToBottom, 50);
    const t2 = setTimeout(scrollToBottom, 150);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [room?.chatLogs, isLobbyChatOpen]);

  const roomRef = useRef(null);
  useEffect(() => {
    roomRef.current = room;
    gameDataRef.current = gameData;
  }, [room, gameData]);

  useEffect(() => {
    setLastDice(null);
  }, [gameData?.activePlayerId]);

  useEffect(() => {
    if (gameData && gameData.phase === 'GAME_OVER') {
      localStorage.removeItem('lastActiveRoomId');
    }
  }, [gameData?.phase]);

  const lastTaxTsRef = useRef(0);
  useEffect(() => {
    if (gameData?.lastTaxAlert && gameData.lastTaxAlert.timestamp > lastTaxTsRef.current) {
      lastTaxTsRef.current = gameData.lastTaxAlert.timestamp;
      playTaxPaySound();
      setTaxAlert({
        scenario: gameData.lastTaxAlert.scenario,
        amount: gameData.lastTaxAlert.amount,
        playerName: gameData.lastTaxAlert.playerName || 'Oyuncu'
      });
    }
  }, [gameData?.lastTaxAlert]);

  const navigateDeed = (currentId, direction) => {
    const currentTile = propertiesData.find(p => p.id === currentId);
    if (!currentTile) return;

    const propState = gameData?.properties[currentId];
    const ownerPlayerId = propState?.ownerPlayerId;

    let targetTiles = [];
    if (ownerPlayerId) {
      targetTiles = propertiesData.filter(p => {
        const pState = gameData?.properties[p.id];
        return pState && pState.ownerPlayerId === ownerPlayerId;
      });
    } else {
      targetTiles = propertiesData.filter(p => {
        const isPurchasable = ['property', 'railroad', 'utility'].includes(p.type);
        const pState = gameData?.properties[p.id];
        return isPurchasable && (!pState || !pState.ownerPlayerId);
      });
      if (targetTiles.length === 0) {
        targetTiles = propertiesData.filter(p => ['property', 'railroad', 'utility'].includes(p.type));
      }
    }

    if (targetTiles.length <= 1) return;
    targetTiles.sort((a, b) => a.id - b.id);

    const currentIndex = targetTiles.findIndex(p => p.id === currentId);
    if (currentIndex === -1) {
      setSelectedPropertyId(targetTiles[0].id);
      return;
    }

    let nextIndex = (currentIndex + direction + targetTiles.length) % targetTiles.length;
    setSelectedPropertyId(targetTiles[nextIndex].id);
  };

  // Handle keyboard left/right arrow navigation for property deed details
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedPropertyId === null || typeof selectedPropertyId !== 'number') return;
      if (e.key === 'ArrowLeft') {
        navigateDeed(selectedPropertyId, -1);
      } else if (e.key === 'ArrowRight') {
        navigateDeed(selectedPropertyId, 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPropertyId, gameData]);

  const [visualPositions, setVisualPositions] = useState({});

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }

    const join = () => {
      const savedName = localStorage.getItem('playerName');
      if (!savedName) {
        setShowNamePrompt(true);
        return;
      }
      let playerId = localStorage.getItem('playerId');
      let reconnectToken = localStorage.getItem('reconnectToken');
      if (playerId === 'undefined' || playerId === 'null') playerId = null;
      if (reconnectToken === 'undefined' || reconnectToken === 'null') reconnectToken = null;

      socket.emit('joinRoom', { 
        roomId: roomId, 
        playerName: savedName, 
        avatar: 'default',
        playerId,
        reconnectToken
      });
    };

    // Join room on mount and automatically on reconnect
    join();
    socket.on('connect', join);

    socket.on('roomJoined', ({ roomId: joinedRoomId, playerId: newPlayerId, reconnectToken: newReconnectToken }) => {
      localStorage.setItem('playerId', newPlayerId);
      localStorage.setItem('reconnectToken', newReconnectToken);
    });

    socket.on('roomUpdated', (updatedRoom) => {
      setRoom(updatedRoom);
    });

    socket.on('gameStarted', (data) => {
      if (data?.phase && data.phase !== 'WAITING_FOR_ROLL' && data.phase !== 'ORDER_ROLL') {
        setIsRolling(false);
      }
      setGameData(prevData => {
        // Cash sound check
        const activePlayerId = localStorage.getItem('playerId');
        if (prevData && activePlayerId) {
          const prevMe = prevData.players.find(p => p.playerId === activePlayerId);
          const newMe = data.players.find(p => p.playerId === activePlayerId);
          if (prevMe && newMe && newMe.money !== prevMe.money) {
            playCashSound();
          }
        }
        return data;
      });
    });

    socket.on('diceRolled', (diceData) => {
      setIsRolling(true);
      playDiceSound();
      
      setTimeout(() => {
        setIsRolling(false);
        setLastDice(diceData);
      }, 1000); // 1 second rolling animation

      setTimeout(() => setIsRolling(false), 1200); // Safety fallback to unblock UI
      setTimeout(() => setLastDice(null), 8000);
    });

    socket.on('gameEvents', (events) => {
      if (!events || !Array.isArray(events)) return;
      events.forEach(evt => {
        if (evt.type === 'PROPERTY_PURCHASED') {
          playSuccessSound();
        } else if (evt.type === 'PLAYER_BANKRUPT' || evt.type === 'PLAYER_SENT_TO_JAIL') {
          playJailSound();
        } else if (evt.type === 'HONK') {
          playHonkSound();
          const pid = evt.data.playerId;
          setActiveHonks(prev => ({ ...prev, [pid]: true }));
          setTimeout(() => {
            setActiveHonks(prev => {
              const next = { ...prev };
              delete next[pid];
              return next;
            });
          }, 1500);
        } else if (evt.type === 'RENT_PAID' || evt.type === 'PROPERTY_UNMORTGAGED') {
          playCashSound();
        } else if (evt.type === 'PLAYER_PASSED_GO') {
          playDoubleSalarySound();
        } else if (evt.type === 'CARD_DRAWN') {
          playCardDrawSound();
        } else if (evt.type === 'TAX_PAID') {
          playTaxPaySound();
          if (evt.data?.scenario) {
            const pObj = gameState?.players?.find(p => p.playerId === evt.data.playerId);
            setTaxAlert({
              scenario: evt.data.scenario,
              amount: evt.data.amount,
              playerName: pObj?.name || 'Oyuncu'
            });
          }
        } else if (evt.type === 'DEBT_STARTED' || evt.type === 'PROPERTY_MORTGAGED') {
          playTaxPaySound();
        } else if (evt.type === 'FREE_PARKING_COLLECTED') {
          playBigWinSound();
        } else if (evt.type === 'HOUSE_BOUGHT' || evt.type === 'HOTEL_BOUGHT') {
          playHotelBuiltSound();
        } else if (evt.type === 'COLOR_GROUP_COMPLETED') {
          triggerConfetti();
          setCompletionAlert({
            playerName: evt.data.playerName,
            color: evt.data.color,
            colorName: evt.data.colorName,
            logMsg: evt.data.logMsg
          });
          setTimeout(() => setCompletionAlert(null), 4500);
        }
      });
    });

    socket.on('kicked', () => {
      alert('Küratör tarafından odadan çıkarıldınız.');
      navigate('/');
    });

    socket.on('error', (err) => {
      showToast(err.message || err);
      if (!roomRef.current) {
        navigate(`/?room=${roomId}`);
      }
    });

    return () => {
      socket.off('connect');
      socket.off('roomUpdated');
      socket.off('gameStarted');
      socket.off('diceRolled');
      socket.off('gameEvents');
      socket.off('kicked');
      socket.off('error');
    };
  }, [navigate, roomId]);

  // Turn timer ticker effect
  useEffect(() => {
    if (!gameData || !gameData.turnTimerExpiresAt || gameData.phase === 'GAME_OVER') {
      setTimeLeft(null);
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((gameData.turnTimerExpiresAt - Date.now()) / 1000));
      setTimeLeft(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [gameData?.turnTimerExpiresAt, gameData?.phase]);

  // Resync socket on app resume / tab visibility change (e.g. returning from phone call)
  useEffect(() => {
    const handleAppResume = () => {
      if (document.visibilityState === 'visible' && roomId) {
        console.log('[Resync] App returned to foreground, requesting sync...');
        if (!socket.connected) {
          socket.connect();
        }
        socket.emit('requestSync', { roomId });
      }
    };

    document.addEventListener('visibilitychange', handleAppResume);
    window.addEventListener('focus', handleAppResume);
    window.addEventListener('pageshow', handleAppResume);

    return () => {
      document.removeEventListener('visibilitychange', handleAppResume);
      window.removeEventListener('focus', handleAppResume);
      window.removeEventListener('pageshow', handleAppResume);
    };
  }, [roomId]);

  useEffect(() => {
    if (!gameData || !isPlaying || isRolling) return;
    
    // Initialize visual positions
    const nextVisuals = { ...visualPositions };
    let updated = false;
    
    gameData.players.forEach(p => {
      if (nextVisuals[p.playerId] === undefined) {
        nextVisuals[p.playerId] = p.position;
        updated = true;
      }
    });
    
    if (updated) {
      setVisualPositions(nextVisuals);
      return;
    }

    // Hopping animation loop
    gameData.players.forEach(p => {
      const currentVisual = visualPositions[p.playerId];
      const actual = p.position;
      
      if (currentVisual !== undefined && currentVisual !== actual && !p.bankrupt) {
        const diff = (actual - currentVisual + 40) % 40;
        if (p.inJail || diff > 12) {
          // Teleport/Jail jump
          setVisualPositions(prev => ({
            ...prev,
            [p.playerId]: actual
          }));
        } else {
          // Step-by-step hopping
          let step = currentVisual;
          const timer = setInterval(() => {
            step = (step + 1) % 40;
            setVisualPositions(prev => ({
              ...prev,
              [p.playerId]: step
            }));
            playStepSound();

            if (step === actual) {
              clearInterval(timer);
              const tile = propertiesData.find(pd => pd.id === actual);
              if (tile) {
                if (tile.type === 'railroad') {
                  if (tile.name.includes('Metro') || tile.name.includes('Aktarma')) {
                    playMetroSound();
                  } else if (tile.name.includes('İskele')) {
                    playVapurDudugu();
                  } else {
                    playIstasyonZili();
                  }
                } else if (tile.id === 12) {
                  playElectricSound();
                } else if (tile.id === 28) {
                  playWaterSound();
                } else if (tile.id === 4) {
                  playCigdemSound();
                }
              }
            }
          }, 250);
        }
      }
    });
  }, [gameData, isPlaying, isRolling]);

  // Synchronize RESOLVE_TILE call with hopping visual animation completion
  useEffect(() => {
    if (!gameData || !isPlaying) return;
    const myPlayerId = localStorage.getItem('playerId');
    
    if (gameData.phase === 'RESOLVING_TILE' && gameData.activePlayerId === myPlayerId) {
      const currentVisual = visualPositions[myPlayerId];
      const actual = gameData.players.find(p => p.playerId === myPlayerId)?.position;
      
      if (currentVisual !== undefined && currentVisual === actual) {
        const timer = setTimeout(() => {
          socket.emit('gameCommand', {
            roomId,
            playerId: myPlayerId,
            command: {
              type: 'RESOLVE_TILE',
              payload: {},
              commandId: Math.random().toString(36).substring(2, 9)
            }
          });
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [gameData?.phase, gameData?.activePlayerId, visualPositions, isPlaying, roomId]);

  // Auto Roll Effect
  useEffect(() => {
    if (!gameData || !isPlaying || !autoRoll) return;
    const myPlayerId = localStorage.getItem('playerId');
    const isMyTurn = gameData.activePlayerId === myPlayerId;
    
    if (isMyTurn && (gameData.phase === 'WAITING_FOR_ROLL' || gameData.phase === 'TURN_START') && !isRolling) {
      const timer = setTimeout(() => {
        sendCommand('ROLL_DICE');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [gameData?.phase, gameData?.activePlayerId, autoRoll, isPlaying, isRolling]);

  // Auto Buy Effect
  useEffect(() => {
    if (!gameData || !isPlaying || !autoBuy) return;
    const myPlayerId = localStorage.getItem('playerId');
    const isMyTurn = gameData.activePlayerId === myPlayerId;
    
    if (isMyTurn && gameData.phase === 'WAITING_FOR_PROPERTY_DECISION' && gameData.pendingAction && gameData.pendingAction.type === 'BUY_OR_AUCTION') {
      const tileId = gameData.pendingAction.tileId;
      const tile = propertiesData?.find(p => p.id === tileId);
      const price = tile ? tile.price : 0;
      const me = gameData.players.find(p => p.playerId === myPlayerId);
      
      if (me && me.money >= price) {
        const timer = setTimeout(() => {
          sendCommand('BUY_PROPERTY', { tileId });
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [gameData?.phase, gameData?.activePlayerId, gameData?.pendingAction, autoBuy, isPlaying]);

  // Auto Resolve Card Effect
  useEffect(() => {
    if (!gameData || !isPlaying || !autoResolveCards) return;
    const myPlayerId = localStorage.getItem('playerId');
    const isMyTurn = gameData.activePlayerId === myPlayerId;
    
    if (isMyTurn && gameData.phase === 'CARD_REVEAL') {
      const timer = setTimeout(() => {
        sendCommand('RESOLVE_CARD');
      }, 1500); // give them 1.5 seconds to read the card
      return () => clearTimeout(timer);
    }
  }, [gameData?.phase, gameData?.activePlayerId, autoResolveCards, isPlaying]);

  // Auto End Turn Effect
  useEffect(() => {
    if (!gameData || !isPlaying) return;
    const myPlayerId = localStorage.getItem('playerId');
    const isMyTurn = gameData.activePlayerId === myPlayerId;
    
    if (isMyTurn && (gameData.phase === 'OPTIONAL_ACTIONS' || gameData.phase === 'TURN_END')) {
      const timer = setTimeout(() => {
        socket.emit('gameCommand', {
          roomId,
          playerId: myPlayerId,
          command: {
            type: 'END_TURN',
            payload: {},
            commandId: Math.random().toString(36).substring(2, 9)
          }
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [gameData?.phase, gameData?.activePlayerId, isPlaying, roomId]);

  const sendCommand = (type, payload = {}) => {
    const playerId = localStorage.getItem('playerId');
    socket.emit('gameCommand', {
      roomId,
      playerId,
      command: {
        type,
        payload,
        commandId: Math.random().toString(36).substring(2, 10)
      }
    });
  };

  const handleSendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    socket.emit('sendChatMessage', { roomId, message: chatInput });
    setChatInput('');
  };

  const handleStartGame = () => {
    socket.emit('startGame', roomId);
  };

  const handleLeave = () => {
    localStorage.removeItem('lastActiveRoomId');
    socket.emit('leaveRoom', { roomId, playerId: myPlayerId });
    socket.disconnect();
    navigate('/');
  };

  const handleTokenChange = (token) => {
    const playerId = localStorage.getItem('playerId');
    socket.emit('changeToken', { roomId, playerId, token });
  };

  const handleSettingChange = (settingKey, value) => {
    const playerId = localStorage.getItem('playerId');
    socket.emit('updateSettings', { roomId, playerId, settings: { [settingKey]: value } });
  };

  const handleToggleReady = () => {
    const me = room.players.find(p => p.playerId === myPlayerId);
    socket.emit('toggleReady', { roomId, playerId: myPlayerId, ready: !me?.ready });
  };

  const handleKickPlayer = (targetPlayerId) => {
    if (window.confirm('Bu oyuncuyu odadan çıkarmak istiyor musunuz?')) {
      socket.emit('removePlayer', { roomId, playerId: myPlayerId, targetPlayerId });
    }
  };

  const handleColorChange = (color) => {
    socket.emit('changeColor', { roomId, playerId: myPlayerId, color });
  };

  const handleAddBot = () => {
    socket.emit('addBot', { roomId, playerId: myPlayerId });
  };

  if (!room || (isPlaying && !gameData)) {
    return (
      <div className="loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'radial-gradient(circle at center, #0f172a 0%, #020617 100%)' }}>
        {showNamePrompt ? (
          <div className="glass-panel" style={{ width: '90%', maxWidth: '400px', padding: '32px', textAlign: 'center', background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(20px)', border: '1.5px solid rgba(251, 191, 36, 0.25)', borderRadius: '24px', boxShadow: '0 20px 50px rgba(0,0,0,0.6)', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <BrandLogo size={64} />
            </div>
            <h2 style={{ color: '#fbbf24', fontSize: '1.5rem', fontWeight: 900, marginBottom: '12px', fontFamily: 'Outfit, sans-serif', letterSpacing: '0.5px' }}>{gameBrand.displayName}</h2>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '24px', fontFamily: 'Outfit, sans-serif', lineHeight: '1.4' }}>Odaya katılmak için bir takma ad seçin.</p>
            
            <div style={{ marginBottom: '24px', textAlign: 'left' }}>
              <label style={{ fontSize: '0.72rem', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '8px', fontFamily: 'Outfit, sans-serif', letterSpacing: '1px' }}>OYUNCU ADI</label>
              <input 
                type="text" 
                placeholder="Adınızı yazın..." 
                value={tempName} 
                onChange={(e) => setTempName(e.target.value)}
                maxLength={15}
                style={{
                  width: '100%',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1.5px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '10px',
                  padding: '12px 14px',
                  color: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontSize: '0.92rem',
                  fontFamily: 'Outfit, sans-serif',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#fbbf24'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.08)'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && tempName.trim()) {
                    localStorage.setItem('playerName', tempName.trim());
                    setShowNamePrompt(false);
                    socket.emit('joinRoom', { 
                      roomId: roomId, 
                      playerName: tempName.trim(), 
                      avatar: 'default',
                      playerId: localStorage.getItem('playerId'),
                      reconnectToken: localStorage.getItem('reconnectToken')
                    });
                  }
                }}
              />
            </div>
            
            <button 
              className="chat-send-btn" 
              style={{ width: '100%', padding: '14px', fontSize: '0.95rem', justifyContent: 'center', borderRadius: '10px' }}
              onClick={() => {
                if (!tempName.trim()) return;
                localStorage.setItem('playerName', tempName.trim());
                setShowNamePrompt(false);
                socket.emit('joinRoom', { 
                  roomId: roomId, 
                  playerName: tempName.trim(), 
                  avatar: 'default',
                  playerId: localStorage.getItem('playerId'),
                  reconnectToken: localStorage.getItem('reconnectToken')
                });
              }}
            >
              Odaya Giriş Yap 🚀
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#fff', fontFamily: 'Outfit, sans-serif' }}>
            <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#fbbf24', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px auto' }}></div>
            <p style={{ fontSize: '0.92rem', color: '#94a3b8' }}>Odaya bağlanılıyor...</p>
          </div>
        )}
      </div>
    );
  }

  const isHost = room.players.find(p => p.playerId === myPlayerId)?.isHost;

  if (isPlaying && gameData) {

    const renderTabbedSidebar = () => {
      return (
        <div className="panel-section tabbed-sidebar-section" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <div className="sidebar-tabs-header" style={{ display: 'flex', borderBottom: '1.5px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.2)' }}>
            <button 
              type="button"
              className={`sidebar-tab-btn ${activeSidebarTab === 'chat' ? 'active' : ''}`}
              onClick={() => {
                setActiveSidebarTab('chat');
                setUnreadChatCount(0);
              }}
              style={{
                flex: 1,
                padding: '12px 8px',
                border: 'none',
                background: activeSidebarTab === 'chat' ? 'rgba(255,255,255,0.03)' : 'transparent',
                color: activeSidebarTab === 'chat' ? '#fbbf24' : '#94a3b8',
                fontFamily: 'Outfit, sans-serif',
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderBottom: activeSidebarTab === 'chat' ? '2.5px solid #fbbf24' : '2.5px solid transparent'
              }}
            >
              💬 SOHBET ODASI {unreadChatCount > 0 && (
                <span style={{
                  backgroundColor: '#ef4444',
                  color: '#fff',
                  fontSize: '0.7rem',
                  padding: '2px 6px',
                  borderRadius: '10px',
                  marginLeft: '4px',
                  fontWeight: 'bold'
                }}>
                  {unreadChatCount}
                </span>
              )}
            </button>
            <button 
              type="button"
              className={`sidebar-tab-btn ${activeSidebarTab === 'ticker' ? 'active' : ''}`}
              onClick={() => setActiveSidebarTab('ticker')}
              style={{
                flex: 1,
                padding: '12px 8px',
                border: 'none',
                background: activeSidebarTab === 'ticker' ? 'rgba(255,255,255,0.03)' : 'transparent',
                color: activeSidebarTab === 'ticker' ? '#fbbf24' : '#94a3b8',
                fontFamily: 'Outfit, sans-serif',
                fontSize: '0.8rem',
                fontWeight: 800,
                cursor: 'pointer',
                transition: 'all 0.2s',
                borderBottom: activeSidebarTab === 'ticker' ? '2.5px solid #fbbf24' : '2.5px solid transparent'
              }}
            >
              📢 SON DUYURULAR
            </button>
          </div>

          {activeSidebarTab === 'chat' ? (
            <div className="tab-pane-content" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div className="panel-chat-logs-container" ref={chatContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
                {gameData.logs && gameData.logs.length > 0 ? (
                  gameData.logs.map((log, idx) => {
                    if (log.startsWith('💬 ')) {
                      const content = log.substring(2);
                      const colonIdx = content.indexOf(':');
                      if (colonIdx !== -1) {
                        const senderName = content.substring(0, colonIdx).trim();
                        const messageText = content.substring(colonIdx + 1);
                        const senderPlayer = gameData.players.find(p => p.name.trim() === senderName);
                        const playerColor = senderPlayer ? senderPlayer.color : '#fff';
                        const playerToken = senderPlayer ? senderPlayer.token : 'Vapur';
                        return (
                          <div key={idx} className="chat-bubble-row">
                            <span className="mini-token-inline" style={{ marginRight: '4px' }}>
                              <TokenIcon token={playerToken} color={playerColor} size={20} />
                            </span>
                            <span className="chat-bubble" style={{ borderLeft: `2.5px solid ${playerColor}`, color: playerColor }}>
                              <strong>{senderName}:</strong> {cleanLogMessage(messageText)}
                            </span>
                          </div>
                        );
                      } else {
                        const matchingPlayer = gameData.players.find(p => content.includes(p.name));
                        const playerColor = matchingPlayer ? matchingPlayer.color : '#fbbf24';
                        const playerToken = matchingPlayer ? matchingPlayer.token : 'Vapur';
                        return (
                          <div key={idx} className="chat-bubble-row system-chat-row">
                            <span className="mini-token-inline" style={{ marginRight: '4px' }}>
                              <TokenIcon token={playerToken} color={playerColor} size={20} />
                            </span>
                            <span 
                              className="chat-bubble system-chat-bubble" 
                              style={{ 
                                borderLeft: `2.5px solid ${playerColor}`, 
                                color: '#e2e8f0', 
                                fontStyle: 'italic', 
                                background: 'rgba(255, 255, 255, 0.05)',
                                fontWeight: 500
                              }}
                            >
                              {cleanLogMessage(content)}
                            </span>
                          </div>
                        );
                      }
                    }
                    return null;
                  })
                ) : (
                  <div className="log-empty">Henüz sohbet yok. İlk yazan sen ol!</div>
                )}
                <div ref={chatEndRef} />
              </div>

              <div className="chat-reactions-inline-row">
                <button 
                  type="button"
                  className={`chat-reaction-btn honk-btn ${honkCooldown ? 'cooldown' : ''}`} 
                  onClick={handleHonkClick} 
                  disabled={honkCooldown}
                  title="Vosvos Kornası Çal"
                >
                  {honkCooldown ? 'Bekle...' : 'HONKKK!'}
                </button>
                <button type="button" className="chat-reaction-btn" onClick={() => socket.emit('sendChatMessage', { roomId, message: '💬 😠 Kızdım!' })}>😠</button>
                <button type="button" className="chat-reaction-btn" onClick={() => socket.emit('sendChatMessage', { roomId, message: '💬 😂 Güldüm!' })}>😂</button>
                <button type="button" className="chat-reaction-btn" onClick={() => socket.emit('sendChatMessage', { roomId, message: '💬 👍 Helal!' })}>👍</button>
              </div>

              <form className="panel-chat-input-form" onSubmit={handleSendChat}>
                {room?.settings?.friendMode && (
                  <div className="quick-chat-wrapper" style={{ position: 'relative' }}>
                    <button 
                      type="button" 
                      className="quick-chat-trigger-btn"
                      onClick={() => setShowQuickChat(!showQuickChat)}
                      title="Hızlı Sohbet Mesajları"
                    >
                      💬
                    </button>
                    {showQuickChat && (
                      <div className="quick-chat-popup-menu">
                        {quickMessages.map((msg, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="quick-chat-menu-item"
                            onClick={() => handleSendQuickMessage(msg)}
                            disabled={quickChatCooldown}
                          >
                            {msg}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <input 
                  type="text" 
                  placeholder="Mesaj yazın..." 
                  value={chatInput} 
                  onChange={(e) => setChatInput(e.target.value)} 
                />
                <button type="submit" className="chat-send-btn">Gönder</button>
              </form>
            </div>
          ) : (
            <div className="tab-pane-content" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', padding: '12px' }}>
              <div className="ticker-scroll-content" ref={tickerContainerRef} style={{ flex: 1, overflowY: 'auto' }}>
                {gameData.logs && gameData.logs.length > 0 ? (
                  gameData.logs
                    .filter(l => !l.startsWith('💬'))
                    .slice(-25)
                    .map((logStr, idx) => {
                      const cleanMsg = cleanLogMessage(logStr);
                      let matchedPlayer = null;
                      for (const p of gameData.players) {
                        if (cleanMsg.includes(p.name)) {
                          matchedPlayer = p;
                          break;
                        }
                      }
                      return (
                        <div key={idx} className="ticker-log-item animate-fade-in" style={{ padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.03)', fontSize: '0.8rem', color: matchedPlayer ? matchedPlayer.color : '#e2e8f0', display: 'flex', gap: '6px', alignItems: 'center' }}>
                          {matchedPlayer ? (
                            <span className="mini-token-inline" style={{ flexShrink: 0 }}>
                              <TokenIcon token={matchedPlayer.token} color={matchedPlayer.color} size={16} />
                            </span>
                          ) : (
                            <span style={{ flexShrink: 0 }}>📢</span>
                          )}
                          <span>{cleanMsg}</span>
                        </div>
                      );
                    })
                ) : (
                  <div className="ticker-log-item">Duyuru bulunmuyor.</div>
                )}
                <div ref={tickerEndRef} />
              </div>
            </div>
          )}
        </div>
      );
    };

    const animatedPlayers = gameData.players.map(p => ({
      ...p,
      position: visualPositions[p.playerId] !== undefined ? visualPositions[p.playerId] : p.position
    }));

    return (
      <div className="game-container">
        {isPortraitMobile && gameData && gameData.phase !== 'LOBBY' && (
          <div className="portrait-orientation-overlay">
            <div className="orientation-card">
              <div className="rotate-icon animate-bounce">🔄</div>
              <h2>Lütfen Cihazınızı Yan Çevirin</h2>
              <p>Oyun tahtası landscape (yatay) modunda en iyi deneyimi sunar.</p>
            </div>
          </div>
        )}
        {toastMessage && (
          <div className="toast-notification-container animate-fade-in" style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 99999,
            pointerEvents: 'none'
          }}>
            <div className="glass-panel" style={{
              background: 'rgba(239, 68, 68, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1.5px solid rgba(255,255,255,0.2)',
              boxShadow: '0 8px 32px rgba(239, 68, 68, 0.4)',
              padding: '12px 24px',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '0.85rem',
              fontWeight: 800,
              fontFamily: 'Outfit, sans-serif',
              textAlign: 'center',
              letterSpacing: '0.3px',
              whiteSpace: 'nowrap'
            }}>
              🚨 {toastMessage.message}
            </div>
          </div>
        )}


          {/* Compact Top Bar */}
        <div className="compact-top-bar">
          <div className="bar-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="bar-logo-wrapper"><BrandLogo size={28} /></span>
            <span className="brand-badge">İZMİR</span>
            <span className="brand-title">{gameBrand.compactName.replace("İZMİR · ", "")}</span>
          </div>
          <div className="bar-info-group">
            {/* Top Bar Player Chips (Ağalar) */}
            <div className="top-bar-players-chips">
              {gameData.players.map(p => (
                <div 
                  key={p.playerId} 
                  className={`top-bar-player-chip ${gameData.activePlayerId === p.playerId ? 'active-turn' : ''} ${p.bankrupt ? 'bankrupt' : ''}`}
                  style={{ borderLeft: `3px solid ${p.color}`, cursor: 'pointer' }}
                  onClick={() => setIsPlayersModalOpen(true)}
                  title={`${p.name} - Mülk Portföyünü ve Detaylarını Gör`}
                >
                  <TokenIcon token={p.token} color={p.color} size={14} />
                  <span className="chip-name">{p.name}{p.playerId === myPlayerId ? ' (Sen)' : ''}</span>
                  <span className="chip-money">{p.bankrupt ? '💀 İFLAS' : `₺${p.money}`}</span>
                </div>
              ))}
            </div>

            <div 
              className={`bar-room-tag clickable-share-tag ${copiedRoom ? 'copied' : ''}`}
              onClick={handleShareRoom}
              title="Oda linkini panoya kopyala"
              style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '5px',
                transition: 'all 0.2s ease',
                ...(copiedRoom ? { background: 'rgba(34, 197, 94, 0.25)', borderColor: '#4ade80', color: '#4ade80' } : {})
              }}
            >
              <span>{copiedRoom ? 'Link Panoya Kopyalandı! ✓' : `ODA: ${roomId}`}</span>
              <Share2 size={13} style={{ color: copiedRoom ? '#4ade80' : '#fbbf24' }} />
            </div>
          </div>
          <div className="bar-actions-group" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
            <button className="gold-action-btn small" onClick={toggleFullscreen} style={{ padding: '6px' }} title="Tam Ekran Modu">
              <Maximize size={18} />
            </button>
            <button className="gold-action-btn small" onClick={() => setShowSettingsModal(true)} style={{ padding: '6px' }} title="Ayarlar">
              <Settings size={18} />
            </button>
            <button className="bar-exit-btn" onClick={handleLeave} title="Odadan Çık" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <LogOut size={15} />
              <span>Çıkış</span>
            </button>
          </div>
        </div>
        
        {/* Dedicated isolated timer progress line bar */}
        {timeLeft !== null && (() => {
          const limit = gameData.settings?.turnLimitSeconds || 30;
          const ratio = Math.max(0, Math.min(1, timeLeft / limit));
          const timerColor = ratio < 0.3 ? '#ef4444' : ratio < 0.6 ? '#eab308' : '#22c55e';
          return (
            <div className="top-bar-timer-wrapper">
              <div 
                className="top-bar-timer-line" 
                style={{ 
                  width: `${ratio * 100}%`,
                  backgroundColor: timerColor,
                  boxShadow: `0 0 8px ${timerColor}`
                }}
              />
            </div>
          );
        })()}
        
        <div className="game-layout">
          {/* LARGE GAME BOARD CONTAINER */}
          <div className="board-viewport-container">
            <Board 
              players={animatedPlayers} 
              properties={gameData.properties}
              activeHonks={activeHonks}
              lastDice={lastDice}
              gameData={gameData}
              room={room}
              isRolling={isRolling}
              myPlayerId={myPlayerId}
              isMyTurn={isMyTurn}
              sendCommand={sendCommand}
              activePlayer={activePlayer}
              me={me}
              isMyBidTurn={isMyBidTurn}
              isMyDebt={isMyDebt}
              auctionBid={auctionBid}
              setAuctionBid={setAuctionBid}
              selectedPropertyId={selectedPropertyId}
              setSelectedPropertyId={setSelectedPropertyId}
              onSelectProperty={(id) => setSelectedPropertyId(id)}
              autoRoll={autoRoll}
              autoBuy={autoBuy}
              showOthersCards={showOthersCards}
              navigateDeed={navigateDeed}
              onOpenPlayers={() => setIsPlayersModalOpen(true)}
              onOpenChat={() => {
                setIsChatOpen(true);
                setUnreadChatCount(0);
              }}
              unreadChatCount={unreadChatCount}
              latestLog={latestLog}
              logVisible={logVisible}
            />
          </div>
        </div>
        {taxAlert && (
          <TaxScenarioModal
            scenario={taxAlert.scenario}
            amount={taxAlert.amount}
            playerName={taxAlert.playerName}
            autoClose={gameData?.settings?.autoCloseTaxCards === true}
            onClose={() => setTaxAlert(null)}
          />
        )}
        {completionAlert && (
          <div className="completion-alert-overlay" style={{
            position: 'absolute',
            top: '25%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
            pointerEvents: 'none'
          }}>
            <div className="glass-panel completion-alert-card" style={{
              background: 'rgba(15, 23, 42, 0.9)',
              backdropFilter: 'blur(20px)',
              border: `2.5px solid ${completionAlert.color || '#f59e0b'}`,
              boxShadow: `0 0 30px ${(completionAlert.color || '#f59e0b')}90`,
              padding: '28px 36px',
              borderRadius: '24px',
              textAlign: 'center',
              maxWidth: '450px'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '12px' }}>👑</div>
              <h3 style={{ margin: 0, color: '#ffffff', fontFamily: 'Outfit, sans-serif', fontSize: '1.4rem', fontWeight: 900, letterSpacing: '1px' }}>SET TAMAMLANDI!</h3>
              <p style={{ margin: '12px 0 0 0', color: '#ffffff', fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem', fontWeight: 700, lineHeight: '1.4' }}>
                {(completionAlert.logMsg || '')
                  .replace(/ev ve otel/gi, 'tezgâh ve ağa yalısı')}
              </p>
            </div>
          </div>
        )}
        <canvas id="confetti-canvas" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 9998
        }} />

        {/* Chat & Announcements Modal Drawer */}
        {isChatOpen && createPortal(
          <div className="modal-overlay animate-fade-in" onClick={() => setIsChatOpen(false)}>
            <div className="modal-backdrop" />
            <div className="modal-content animate-slide-up" style={{ width: '92%', maxWidth: '520px', height: '80vh' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>💬 Kordon Sohbet & Duyurular</h3>
                <button className="close-btn" onClick={() => setIsChatOpen(false)}>✕</button>
              </div>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {renderTabbedSidebar()}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Players Status & Property Portfolios Modal Drawer */}
        {isPlayersModalOpen && createPortal(
          <div className="modal-overlay animate-fade-in" onClick={() => setIsPlayersModalOpen(false)}>
            <div className="modal-backdrop" />
            <div className="modal-content animate-slide-up" style={{ width: '94%', maxWidth: '580px', maxHeight: '88vh', overflowY: 'auto', background: '#0f172a', border: '1.5px solid #BF953F', borderRadius: '16px', padding: '16px' }} onClick={e => e.stopPropagation()}>
              <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid rgba(251, 191, 36, 0.3)', paddingBottom: '10px' }}>
                <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: '#fbbf24', fontSize: '1.15rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  👑 Ağalar & Mülk Portföyü
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button 
                    onClick={() => setShowSettingsModal(true)} 
                    style={{ background: 'rgba(251, 191, 36, 0.2)', border: '1px solid #fbbf24', color: '#fbbf24', fontSize: '0.8rem', padding: '4px 10px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 700 }}
                  >
                    ⚙️ Ayarlar
                  </button>
                  <button className="close-btn" onClick={() => setIsPlayersModalOpen(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: '1rem', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer' }}>✕</button>
                </div>
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {gameData.players.map(p => {
                  const ownedPropEntries = Object.entries(gameData.properties || {}).filter(([id, pState]) => (pState.ownerPlayerId === p.playerId || pState.owner === p.playerId));
                  const houseCount = ownedPropEntries.reduce((acc, [id, pState]) => acc + (pState.houses || 0), 0);
                  const hotelCount = ownedPropEntries.reduce((acc, [id, pState]) => acc + (pState.hotel ? 1 : 0), 0);
                  
                  const propsValue = ownedPropEntries.reduce((acc, [id, pState]) => {
                    const prop = propertiesData.find(b => b.id === Number(id));
                    let val = prop?.price || 0;
                    if (pState.houses) val += pState.houses * (prop?.houseCost || 100);
                    if (pState.hotel) val += 5 * (prop?.houseCost || 100);
                    return acc + val;
                  }, 0);
                  const netWorth = p.money + propsValue;
                  const jailCardsCount = p.jailFreeCards?.length || 0;
                  const isTurn = gameData.activePlayerId === p.playerId;

                  const colorMap = {
                    brown: '#78350f',
                    cyan: '#06b6d4',
                    pink: '#ec4899',
                    orange: '#f97316',
                    red: '#ef4444',
                    yellow: '#eab308',
                    green: '#10b981',
                    blue: '#3b82f6'
                  };

                  return (
                    <div 
                      key={p.playerId} 
                      className={`compact-player-card ${isTurn ? 'active-turn' : ''} ${p.bankrupt ? 'bankrupt' : ''}`}
                      style={{ 
                        background: 'rgba(30, 41, 59, 0.75)', 
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderLeftWidth: '5px',
                        borderLeftStyle: 'solid',
                        borderLeftColor: p.color,
                        padding: '14px', 
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                      }}
                    >
                      {/* Player Header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <TokenIcon token={p.token} color={p.color} size={24} />
                          <strong style={{ color: p.color, fontSize: '1rem', fontFamily: 'Outfit, sans-serif' }}>
                            {p.name} {p.playerId === myPlayerId && '(Sen)'}
                          </strong>
                          {isTurn && (
                            <span style={{ background: '#fbbf24', color: '#0f172a', fontSize: '0.68rem', fontWeight: 900, padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase' }}>
                              🎲 SIRA ONDA
                            </span>
                          )}
                          {p.inJail && (
                            <span style={{ background: '#ef4444', color: '#fff', fontSize: '0.68rem', fontWeight: 900, padding: '2px 6px', borderRadius: '4px' }}>
                              ⛓️ SÜRGÜNDE
                            </span>
                          )}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 900, color: '#10b981', fontSize: '1.05rem', fontFamily: 'Outfit, sans-serif' }}>₺{p.money}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Servet: <strong style={{ color: '#fbbf24' }}>₺{netWorth}</strong></div>
                        </div>
                      </div>

                      {/* Jail Free Cards Badge */}
                      {jailCardsCount > 0 && (
                        <div 
                          onClick={() => {
                            setIsPlayersModalOpen(false);
                            setSelectedPropertyId('jail_card');
                          }}
                          style={{ 
                            background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(202, 138, 4, 0.3) 100%)',
                            border: '1.5px solid #fbbf24',
                            borderRadius: '8px',
                            padding: '6px 10px',
                            margin: '8px 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer'
                          }}
                          title="Karakol Tahliye Kartı Detayını İncele"
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', fontWeight: 800, color: '#fef08a' }}>
                            <span>🔑</span>
                            <span>Karakol Tahliye Kartı</span>
                            <span style={{ background: '#fbbf24', color: '#0f172a', padding: '1px 6px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 900 }}>{jailCardsCount} Adet</span>
                          </div>
                          <span style={{ fontSize: '0.7rem', color: '#fbbf24', textDecoration: 'underline' }}>Görüntüle 🔍</span>
                        </div>
                      )}

                      {/* Owned Properties List Grid */}
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ fontSize: '0.75rem', color: '#cbd5e1', fontWeight: 700, marginBottom: '6px', display: 'flex', justifyContent: 'space-between' }}>
                          <span>Sahip Olunan Mülkler ({ownedPropEntries.length}):</span>
                          {(houseCount > 0 || hotelCount > 0) && (
                            <span style={{ color: '#fbbf24', fontSize: '0.7rem' }}>
                              {houseCount > 0 && `🏠 ${houseCount} Tezgâh `}
                              {hotelCount > 0 && `🏨 ${hotelCount} Yalı`}
                            </span>
                          )}
                        </div>

                        {ownedPropEntries.length > 0 ? (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '6px' }}>
                            {ownedPropEntries.map(([id, pState]) => {
                              const propId = Number(id);
                              const prop = propertiesData.find(b => b.id === propId);
                              const cardColor = prop?.color ? colorMap[prop.color] : '#0284c7';
                              const isMortgaged = pState.isMortgaged;
                              const houses = pState.houses || 0;
                              const hotel = pState.hotel;

                              return (
                                <div 
                                  key={propId} 
                                  onClick={() => {
                                    setIsPlayersModalOpen(false);
                                    setSelectedPropertyId(propId);
                                  }}
                                  style={{ 
                                    background: 'rgba(15, 23, 42, 0.85)', 
                                    border: `1.5px solid ${isMortgaged ? '#ef4444' : cardColor}`, 
                                    borderRadius: '6px', 
                                    padding: '6px 8px',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                  }}
                                  title={`${prop?.name || 'Mülk'} - Kartı detaylı incele / işlem yap`}
                                >
                                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: cardColor }} />
                                  <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#fff', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {prop?.name || `Mülk #${propId}`}
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '0.65rem' }}>
                                    <span style={{ color: '#94a3b8' }}>₺{prop?.price || 0}</span>
                                    {isMortgaged ? (
                                      <span style={{ color: '#ef4444', fontWeight: 800 }}>İPOTEKLİ</span>
                                    ) : hotel ? (
                                      <span style={{ color: '#ec4899', fontWeight: 900 }}>🏨 YALI</span>
                                    ) : houses > 0 ? (
                                      <span style={{ color: '#10b981', fontWeight: 800 }}>🏠x{houses}</span>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic', background: 'rgba(0,0,0,0.2)', padding: '6px 10px', borderRadius: '6px' }}>
                            Henüz hiç mülk edinmedi.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* SETTINGS MODAL */}
        {showSettingsModal && createPortal(
          <SettingsModal 
            onClose={() => setShowSettingsModal(false)}
            autoRoll={autoRoll}
            onToggleAutoRoll={handleToggleAutoRoll}
            autoBuy={autoBuy}
            onToggleAutoBuy={handleToggleAutoBuy}
            autoResolveCards={autoResolveCards}
            onToggleAutoResolveCards={handleToggleAutoResolveCards}
            showOthersCards={showOthersCards}
            onToggleShowOthersCards={handleToggleShowOthersCards}
            soundFxEnabled={soundFx}
            onToggleSoundFx={handleToggleSoundFx}
          />,
          document.body
        )}
      </div>
    );
  }

  const renderLobbyChatPanelContent = (isDrawer = false) => {
    return (
      <div className={`glass-panel lobby-chat-panel ${isDrawer ? 'in-drawer' : ''}`} style={isDrawer ? {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'transparent',
        padding: 0,
        borderRadius: 0,
        border: 'none',
        boxShadow: 'none',
        boxSizing: 'border-box'
      } : {
        width: '380px',
        display: 'flex',
        flexDirection: 'column',
        height: '600px',
        maxHeight: '80vh',
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(30px)',
        padding: '1.5rem',
        borderRadius: '20px',
        border: '1.5px solid var(--border-glass)',
        boxShadow: 'var(--shadow-premium)',
        boxSizing: 'border-box'
      }}>
        {!isDrawer && (
          <h3 style={{ margin: '0 0 1rem 0', fontFamily: 'Outfit, sans-serif', color: '#fbbf24', fontSize: '1.1rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
            💬 SOHBET
          </h3>
        )}
        
        <div className="lobby-chat-messages" ref={lobbyChatContainerRef} style={{
          flex: 1,
          overflowY: 'auto',
          background: 'rgba(0, 0, 0, 0.25)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          padding: '12px',
          marginBottom: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {room.chatLogs && room.chatLogs.length > 0 ? (
            room.chatLogs.map((log, idx) => {
              const content = log.substring(2);
              const colonIdx = content.indexOf(':');
              if (colonIdx !== -1) {
                const senderName = content.substring(0, colonIdx).trim();
                const messageText = content.substring(colonIdx + 1);
                const senderPlayer = room.players.find(p => p.name.trim() === senderName);
                const playerColor = senderPlayer ? senderPlayer.color : '#fff';
                return (
                  <div key={idx} style={{ fontSize: '0.85rem', lineHeight: '1.4', textAlign: 'left' }}>
                    <span style={{ color: playerColor, fontWeight: 700 }}>{senderName}:</span>
                    <span style={{ color: '#e2e8f0', marginLeft: '6px' }}>{messageText}</span>
                  </div>
                );
              }
              return (
                <div key={idx} style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic', textAlign: 'left' }}>
                  {log}
                </div>
              );
            })
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', marginTop: '20px' }}>
              Henüz mesaj yazılmamış. İlk yazan sen ol!
            </div>
          )}
          <div ref={lobbyChatEndRef} />
        </div>

        <form onSubmit={handleSendChat} style={{ display: 'flex', gap: '8px' }} autoComplete="off" data-lpignore="true">
          <input 
            type="text" 
            name="chatmessage"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-lpignore="true"
            placeholder="Mesaj yazın..." 
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            style={{
              flex: 1,
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: '#fff',
              fontSize: '0.85rem',
              outline: 'none',
              textAlign: 'left'
            }}
          />
          <button 
            type="submit" 
            style={{
              background: 'linear-gradient(135deg, #fbbf24, #d97706)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 14px',
              color: '#0f172a',
              fontSize: '0.85rem',
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'opacity 0.2s'
            }}
          >
            Gönder
          </button>
        </form>
      </div>
    );
  };

  // Waiting Room
  const requiredCount = room.settings?.playerCount || 3;
  const isLobbyFull = room.players.length === requiredCount;
  const allReady = room.players.every(p => p.ready);
  const myLobbyPlayer = room.players.find(p => p.playerId === myPlayerId);

  return (
    <div className="waiting-room-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', minHeight: '100vh', padding: '20px', boxSizing: 'border-box', overflow: 'hidden' }}>
      <div className="glass-panel waiting-panel" style={{ margin: 0 }}>
        <div className="room-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button className="icon-btn" onClick={handleLeave} title="Odadan Çık">
              <ArrowLeft size={20} />
            </button>
            <button className="icon-btn" onClick={toggleFullscreen} title="Tam Ekran">
              <Maximize size={20} />
            </button>
            <button className="icon-btn" onClick={() => setShowSettingsModal(true)} title="Kişisel Ayarlar" style={{ color: '#fbbf24' }}>
              <Settings size={20} />
            </button>
            <button 
              className="icon-btn" 
              onClick={toggleLanguage} 
              title="Dil Değiştir / Switch Language"
              style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', padding: '4px 8px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.4)', background: 'rgba(56, 189, 248, 0.1)' }}
            >
              {lang === 'tr' ? '🇬🇧 EN' : '🇹🇷 TR'}
            </button>
          </div>
          <h2 style={{ margin: 0 }}>Oda Kodu: <span className="highlight">{roomId}</span></h2>
        </div>

        <div className="players-list">
          <h3>
            <Users size={18} /> Oyuncular ({room.players.length}/{requiredCount})
          </h3>
          <ul>
            {room.players.map((player) => (
              <li key={player.playerId} className="player-item">
                <div className="player-avatar" style={{ border: `2.5px solid ${player.color}`, boxShadow: `0 0 12px ${player.color}50`, backgroundColor: 'transparent' }}>
                  <TokenIcon token={player.token || 'Vapur'} color={player.color} size={38} />
                </div>
                <span className="player-name">
                  {player.name} {player.playerId === myPlayerId && ' (Sen)'}
                  {player.isBot && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
                      <span className="bot-badge">BOT</span>
                      {isHost ? (
                        <select 
                          value={player.difficulty || 'medium'}
                          onChange={(e) => socket.emit('updateBotDifficulty', { roomId, hostPlayerId: myPlayerId, botPlayerId: player.playerId, difficulty: e.target.value })}
                          style={{
                            background: '#0f172a',
                            color: '#fbbf24',
                            border: '1px solid rgba(251, 191, 36, 0.4)',
                            borderRadius: '6px',
                            padding: '2px 6px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          <option value="easy">🟢 Kolay</option>
                          <option value="medium">🟡 Orta</option>
                          <option value="hard">🔴 Zor</option>
                        </select>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: '#fbbf24', fontWeight: 700 }}>
                          {player.difficulty === 'easy' ? '🟢 Kolay' : player.difficulty === 'hard' ? '🔴 Zor' : '🟡 Orta'}
                        </span>
                      )}
                    </div>
                  )}
                  <span className={`ready-badge ${player.ready ? 'is-ready' : 'is-not-ready'}`}>
                    {player.ready ? ' [Hazır]' : ' [Hazırlanıyor...]'}
                  </span>
                </span>
                
                {player.playerId === myPlayerId ? (
                  <div className="player-customization">
                    <div className="lobby-selectors-wrapper">
                      <div className="lobby-selector-group">
                        <span className="lobby-select-lbl">PİYON</span>
                        <div className="token-grid-selection">
                          {['Vapur', 'SaatKulesi', 'Marti', 'Boyoz', 'NazarBoncugu', 'Palmiye', 'KirmiziAraba', 'Gevrek'].map(tk => {
                            const isTokenTaken = room.players.some(p => p.token === tk && p.playerId !== myPlayerId);
                            return (
                              <button 
                                key={tk} 
                                className={`token-select-btn ${player.token === tk ? 'active' : ''} ${isTokenTaken ? 'disabled-taken' : ''}`}
                                onClick={() => !isTokenTaken && handleTokenChange(tk)}
                                disabled={isTokenTaken}
                                title={isTokenTaken ? `${tokenNamesMap[tk] || tk} (Alındı)` : tokenNamesMap[tk] || tk}
                              >
                                <TokenIcon token={tk} color={isTokenTaken ? '#475569' : player.color} size="var(--lobby-token-select-size, 36px)" />
                                <span className="token-name-label">{tokenNamesMap[tk] || tk}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="lobby-selector-group" style={{ marginTop: '8px' }}>
                        <span className="lobby-select-lbl">RENK</span>
                        <div className="color-grid-selection">
                          {[
                            { hex: '#D4AF37', name: 'Altın' },
                            { hex: '#a855f7', name: 'Mor' },
                            { hex: '#0d9488', name: 'Teal' },
                            { hex: '#cbd5e1', name: 'Platin' },
                            { hex: '#f43f5e', name: 'Fuşya' },
                            { hex: '#475569', name: 'Gece' }
                          ].map(cl => {
                            const isTaken = room.players.some(p => p.playerId !== myPlayerId && p.color === cl.hex);
                            return (
                              <button
                                key={cl.hex}
                                className={`color-select-btn ${player.color === cl.hex ? 'active' : ''} ${isTaken ? 'taken' : ''}`}
                                style={{ backgroundColor: cl.hex }}
                                onClick={() => !isTaken && handleColorChange(cl.hex)}
                                disabled={isTaken}
                                title={isTaken ? `${cl.name} (Alındı)` : cl.name}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}

                {player.isHost && <Crown size={16} className="host-icon" />}
                
                {isHost && !player.isHost && (
                  <button 
                    className="kick-button" 
                    onClick={() => handleKickPlayer(player.playerId)}
                    style={{
                      background: 'rgba(239, 68, 68, 0.12)',
                      border: '1.5px solid rgba(239, 68, 68, 0.4)',
                      color: '#fca5a5',
                      padding: '5px 12px',
                      borderRadius: '8px',
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.2s',
                      fontFamily: 'Outfit, sans-serif',
                      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)',
                      marginLeft: 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.25)';
                      e.currentTarget.style.borderColor = '#ef4444';
                      e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                      e.currentTarget.style.color = '#fca5a5';
                    }}
                  >
                    Kov 🥾
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="room-settings">
          <h3><Settings size={18} /> Oda Ayarları</h3>
          <div className="settings-grid">
            <CustomDropdown
              label="Şehir / Harita Seçimi"
              value={room.settings?.city || 'izmir'}
              options={[
                { value: 'izmir', label: '🏰 İzmir - Kordon Ağaları' },
                { value: 'istanbul', label: '🌉 İstanbul - Boğaz Ağaları' },
                { value: 'ankara', label: '🏛️ Ankara - Çankaya Ağaları' }
              ]}
              onChange={(val) => handleSettingChange('city', val)}
              disabled={!isHost}
              tooltip="Oyun haritasını, mülk isimlerini, şans kartlarını ve şehir temasını belirler."
            />

            <CustomDropdown
              label="Oyuncu Sayısı"
              value={room.settings?.playerCount || 3}
              options={[
                { value: 2, label: '2 Kişilik Oyun' },
                { value: 3, label: '3 Kişilik Oyun' },
                { value: 4, label: '4 Kişilik Oyun' },
                { value: 5, label: '5 Kişilik Oyun' },
                { value: 6, label: '6 Kişilik Oyun' },
                { value: 7, label: '7 Kişilik Oyun' },
                { value: 8, label: '8 Kişilik Oyun' }
              ]}
              onChange={(val) => handleSettingChange('playerCount', val)}
              disabled={!isHost}
              tooltip="Oyuna katılabilecek maksimum oyuncu sayısını belirler."
            />

            <CustomDropdown
              label="Başlangıç Parası"
              value={room.settings?.startMoney || 1500}
              options={[
                { value: 1000, label: '₺1000 (Zor Oyun)' },
                { value: 1500, label: '₺1500 (Klasik)' },
                { value: 2000, label: '₺2000 (Zengin)' },
                { value: 2500, label: '₺2500 (Hızlı)' },
                { value: 5000, label: '₺5000 (Ağa Parası)' },
                { value: 7500, label: '₺7500 (Büyük Sermaye)' },
                { value: 10000, label: '₺10,000 (Holding Ağası)' },
                { value: 15000, label: '₺15,000 (Milyarder)' },
                { value: 20000, label: '₺20,000 (Sınırsız Servet)' }
              ]}
              onChange={(val) => handleSettingChange('startMoney', val)}
              disabled={!isHost}
              tooltip="Oyuna başlarken her oyuncuya verilecek bakiye miktarını belirler."
            />

            <CustomDropdown
              label="Sürgün Kefareti"
              value={room.settings?.jailFineAmount || 50}
              options={[
                { value: 50, label: '₺50 (Klasik)' },
                { value: 100, label: '₺100 (Orta)' },
                { value: 200, label: '₺200 (Zorlu)' }
              ]}
              onChange={(val) => handleSettingChange('jailFineAmount', val)}
              disabled={!isHost}
              tooltip="Çiğli sürgününden kefaret ödeyerek çıkmak için gereken TL miktarı."
            />

            <CustomDropdown
              label="Kira Çarpanı"
              value={room.settings?.rentMultiplier || 1}
              options={[
                { value: 1.0, label: '1.0x (Klasik)' },
                { value: 1.5, label: '1.5x (Zengin)' },
                { value: 2.0, label: '2.0x (Kaos!)' }
              ]}
              onChange={(val) => handleSettingChange('rentMultiplier', val)}
              disabled={!isHost}
              tooltip="Tüm mülklerin kira bedellerini katlayarak oyunu hızlandırır."
            />

            <CustomDropdown
              label="İşlem Süresi (Zamanlayıcı)"
              value={room.settings?.turnLimitSeconds !== undefined ? room.settings.turnLimitSeconds : 30}
              options={[
                { value: 0, label: 'Sınırsız (Yok)' },
                { value: 15, label: '15 Saniye' },
                { value: 30, label: '30 Saniye' },
                { value: 45, label: '45 Saniye' },
                { value: 60, label: '60 Saniye' }
              ]}
              onChange={(val) => handleSettingChange('turnLimitSeconds', val)}
              disabled={!isHost}
              tooltip="Oyuncuların her eylem için sahip olduğu süreyi belirler. Süre bitince otomatik oynatılır."
            />

            <CustomDropdown
              label={t('settings.unlimited_houses', 'Sınırsız Ev / Yalı Deposu')}
              value={room.settings?.unlimitedHouses ? 'true' : 'false'}
              options={[
                { value: 'false', label: 'Kapalı (Klasik 32 Ev / 12 Otel)' },
                { value: 'true', label: 'Açık (Sınırsız Ev / Otel Stoğu)' }
              ]}
              onChange={(val) => handleSettingChange('unlimitedHouses', val === 'true')}
              disabled={!isHost}
              tooltip="Açık olduğunda bankadaki ev ve otel stok limiti kalkar, parası yeten herkes dilediğince bina inşa edebilir."
            />

            <CustomDropdown
              label={t('settings.unlimited_building_level', 'Sınırsız Bina Seviyesi (5+ Ev / Yalı)')}
              value={room.settings?.unlimitedBuildingLevel ? 'true' : 'false'}
              options={[
                { value: 'false', label: 'Kapalı (Maks 1 Yalı / 5 Ev)' },
                { value: 'true', label: 'Açık (Otel Üstüne Sınırsız Tezgâh/Kat)' }
              ]}
              onChange={(val) => handleSettingChange('unlimitedBuildingLevel', val === 'true')}
              disabled={!isHost}
              tooltip="Açık olduğunda otel yapıldıktan sonra da üzerine bina dikmeye devam edilebilir, kira seviyesi her katta artar."
            />

            <CustomDropdown
              label={t('settings.uneven_building', 'Serbest / Eşitsiz İnşaat Dağılımı')}
              value={room.settings?.unevenBuilding ? 'true' : 'false'}
              options={[
                { value: 'false', label: 'Kapalı (Eşit Dağılım Şartı)' },
                { value: 'true', label: 'Açık (Tek Mülke Yığma Serbest)' }
              ]}
              onChange={(val) => handleSettingChange('unevenBuilding', val === 'true')}
              disabled={!isHost}
              tooltip="Açık olduğunda tekele sahip olduğunuz 3 kareden birine diğerleri 0 ev olsa bile dilediğiniz kadar ev dizebilirsiniz."
            />

            <CustomDropdown
              label={t('settings.build_without_monopoly', 'Tekel / Set Şartı Olmadan İnşaat')}
              value={room.settings?.buildWithoutMonopoly ? 'true' : 'false'}
              options={[
                { value: 'false', label: 'Kapalı (Tüm Set Şartı)' },
                { value: 'true', label: 'Açık (Tek Mülke De İnşaat Yapılabilir)' }
              ]}
              onChange={(val) => handleSettingChange('buildWithoutMonopoly', val === 'true')}
              disabled={!isHost}
              tooltip="Açık olduğunda rengin 3 tapusuna da sahip olmadan, sahip olduğunuz tek bir tapuya bile hemen ev kurabilirsiniz."
            />

            <CustomDropdown
              label={t('settings.build_on_special_tiles', 'İstasyon & Kurumlara Ev Kurma (2x Çarpan)')}
              value={room.settings?.buildOnSpecialTiles ? 'true' : 'false'}
              options={[
                { value: 'false', label: 'Kapalı (Sadece Standart Mülkler)' },
                { value: 'true', label: 'Açık (Metro/İZSU/Gediz\'e Ev Dikeyim)' }
              ]}
              onChange={(val) => handleSettingChange('buildOnSpecialTiles', val === 'true')}
              disabled={!isHost}
              tooltip="Açık olduğunda Metro, İskele, İZSU ve Gediz Elektrik gibi kadelere ev kurulabilir. Ev fiyatı istasyon fiyatıdır, her ev kirayı 2 katına çıkarır!"
            />

            <CustomDropdown
              label={t('settings.strict_turn_actions', 'İnşaat ve Takas İçin Kendi Sırasını Bekleme Şartı')}
              value={room.settings?.strictTurnOrderForActions !== false ? 'true' : 'false'}
              options={[
                { value: 'true', label: 'Açık (Sadece Kendi Sırasında Yapılabilir)' },
                { value: 'false', label: 'Kapalı (Her An İnşaat/Takas Yapılabilir)' }
              ]}
              onChange={(val) => handleSettingChange('strictTurnOrderForActions', val === 'true')}
              disabled={!isHost}
              tooltip="Açık (Varsayılan) olduğunda oyuncular sadece kendi sıraları geldiğinde ev kurabilir veya takas teklif edebilir."
            />

            <CustomDropdown
              label={t('settings.loan_shark_mode', 'Tefeci Modu (Acil Borç & Yüksek Faiz)')}
              value={room.settings?.loanSharkMode ? 'true' : 'false'}
              options={[
                { value: 'false', label: 'Kapalı (Standart Bankacılık)' },
                { value: 'true', label: 'Açık (Tefeciden Borç Alınabilir)' }
              ]}
              onChange={(val) => handleSettingChange('loanSharkMode', val === 'true')}
              disabled={!isHost}
              tooltip="Açık olduğunda oyuncular Tefeci Necmi'den ₺2,000 acil nakit borç alabilir. 4 tur boyunca her tur ₺1,000 taksit ödenir (Toplam ₺4,000). Ödeyemeyen anında batar!"
            />

            <CustomDropdown
              label="Bankaya Tapu Satışı (%75 Fiyatla)"
              value={room.settings?.sellPropertyToBankAt75Percent !== false ? 'true' : 'false'}
              options={[
                { value: 'true', label: 'Açık (Tapular %75 Fiyatına Bankaya Satılabilir)' },
                { value: 'false', label: 'Kapalı (Bankaya Tapu Satışı Yok)' }
              ]}
              onChange={(val) => handleSettingChange('sellPropertyToBankAt75Percent', val === 'true')}
              disabled={!isHost}
              tooltip="Açık (Varsayılan) olduğunda oyuncular diledikleri zaman sahip oldukları tapuyu orijinal değerinin %75'i karşılığında bankaya geri satabilir."
            />

            <CustomDropdown
              label="Ceza Kartlarını Otomatik Kapat"
              value={room.settings?.autoCloseTaxCards ? 'true' : 'false'}
              options={[
                { value: 'false', label: 'Kapalı (Kişi Butona Basana Kadar Kart Açık Kalır)' },
                { value: 'true', label: 'Açık (6 Saniye Sonra Otomatik Kapanır)' }
              ]}
              onChange={(val) => handleSettingChange('autoCloseTaxCards', val === 'true')}
              disabled={!isHost}
              tooltip="Kapalı (Varsayılan) olduğunda ceza ve haber kartları oyuncu 'Eyvallah Ağa, Öde' butonuna basana kadar ekranda açık bekler."
            />

            <div style={{ gridColumn: 'span 2' }}>
              <CustomDropdown
                label="Oyun Modu"
                value={room.settings?.gameMode || 'classic'}
                options={[
                  { value: 'classic', label: 'Uzun Gece (Classic)', desc: '🎲 Standart kurallarla uzun soluklu tam ağalık mücadelesi.' },
                  { value: 'fast', label: 'Gevrek Arası (Fast)', desc: '⏱️ Hızlı oyun. Başlangıç parası otomatik ₺2500 olarak ayarlanır.' },
                  { value: 'houseRules', label: 'İzmir Usulü (House Rules)', desc: '🏠 Vergiler otoparkta (Kordon Molası) birikir, başlangıçtan tam geçişte çift maaş verilir.' }
                ]}
                onChange={(val) => {
                  handleSettingChange('gameMode', val);
                  if (val === 'fast') {
                    socket.emit('updateSettings', { roomId, playerId: myPlayerId, settings: { gameMode: val, startMoney: 2500 } });
                  } else if (val === 'houseRules') {
                    socket.emit('updateSettings', { roomId, playerId: myPlayerId, settings: { gameMode: val, freeParkingPool: true, doubleGo: true } });
                  }
                }}
                disabled={!isHost}
                tooltip="Farklı kural kombinasyonları ve hız modları arasında seçim yapın."
              />
            </div>

            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={room.settings?.doubleGo || false} 
                onChange={(e) => handleSettingChange('doubleGo', e.target.checked)}
                disabled={!isHost}
              />
              <span className="checkbox-custom"></span>
              <span>Başlangıçtan Tam Geçişte 2x Maaş</span>
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">Başlangıç karesinden tam olarak geçildiğinde ₺200 yerine ₺400 maaş alınmasını sağlar.</span>
              </span>
            </label>
            
            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={room.settings?.freeParkingPool || false} 
                onChange={(e) => handleSettingChange('freeParkingPool', e.target.checked)}
                disabled={!isHost}
              />
              <span className="checkbox-custom"></span>
              <span>Vergiler Otoparkta Biriksin</span>
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">Ödenen tüm cezalar ve vergiler Kordon Molası (Otopark) havuzunda birikir. Buraya gelen havuzu toplar!</span>
              </span>
            </label>
            
            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={room.settings?.returnBankruptPropertiesToBank !== false} 
                onChange={(e) => handleSettingChange('returnBankruptPropertiesToBank', e.target.checked)}
                disabled={!isHost}
              />
              <span className="checkbox-custom"></span>
              <span>İflasta Mülkleri Bankaya İade Et</span>
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">İflas eden oyuncunun mülklerindeki ipotekler kaldırılır ve mülkler diğer oyuncuların satın alması için bankaya döner.</span>
              </span>
            </label>
            
            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={room.settings?.threeDoublesJail !== false} 
                onChange={(e) => handleSettingChange('threeDoublesJail', e.target.checked)}
                disabled={!isHost}
              />
              <span className="checkbox-custom"></span>
              <span>Ardarda 3 Çift Zarda Sürgüne Gönder</span>
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">Ardarda 3 kere çift zar atan oyuncu turunu bitiremeden direkt Çiğli'ye sürgüne gönderilir.</span>
              </span>
            </label>
            
            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={room.settings?.allowDebtPropertySale !== false} 
                onChange={(e) => handleSettingChange('allowDebtPropertySale', e.target.checked)}
                disabled={!isHost}
              />
              <span className="checkbox-custom"></span>
              <span>Borç Karşılığı Arsa Satış Önerisi</span>
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">Nakit ve ipotek borcu ödemeye yetmediğinde mülkleri alacaklıya satarak borcu kapatma imkanı tanır.</span>
              </span>
            </label>

            <label className="setting-item">
              <input 
                type="checkbox" 
                checked={room.settings?.allowDebtTrade !== false} 
                onChange={(e) => handleSettingChange('allowDebtTrade', e.target.checked)}
                disabled={!isHost}
              />
              <span className="checkbox-custom"></span>
              <span>Borç Ödeme Ekranında Takas Yetkisi</span>
              <span className="tooltip-container">
                <span className="tooltip-icon">?</span>
                <span className="tooltip-text">İflas aşamasındaki oyuncunun diğer oyuncularla takas yaparak mülk satıp nakit bulmasına izin verir.</span>
              </span>
            </label>
          </div>
        </div>

        <div className="lobby-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', width: '100%' }}>
          {!isHost && (
            <button 
              className={`primary-button ready-btn ${myLobbyPlayer?.ready ? 'active-ready' : ''}`}
              onClick={handleToggleReady}
            >
              {myLobbyPlayer?.ready ? 'Hazır Değilim ❌' : 'Hazırım! 👍'}
            </button>
          )}

          {isHost && !isLobbyFull && (
            <button 
              className="primary-button bot-btn" 
              onClick={handleAddBot}
              style={{ background: 'linear-gradient(135deg, #2563EB, #1D4ED8)' }}
            >
              Bot Ekle 🤖
            </button>
          )}

          {isHost ? (
            <button 
              className="primary-button start-btn" 
              onClick={handleStartGame}
              disabled={!isLobbyFull || !allReady}
            >
              <Play size={20} /> Oyunu Başlat
            </button>
          ) : (
            <div className="waiting-text" style={{ textAlign: 'center', fontSize: '0.9rem', color: '#ccc' }}>
              Oyunu başlatmak için tüm oyuncuların hazır olması ve oda sınırına ({requiredCount} oyuncu) ulaşılması gerekir.
            </div>
          )}

          {isHost && !isLobbyFull && (
            <p className="hint-text" style={{ textAlign: 'center', color: '#ffcc00' }}>
              Başlamak için oda sınırına ({requiredCount} oyuncu) kadar oyuncu/bot ekleyin veya bekleyin. (Şu an: {room.players.length})
            </p>
          )}
          {isHost && isLobbyFull && !allReady && (
            <p className="hint-text" style={{ textAlign: 'center', color: '#ffcc00' }}>
              Tüm oyuncuların hazır olması bekleniyor.
            </p>
          )}
        </div>
      </div>

      {/* Lobby Chat Panel */}
      {!isLobbyChatOpen && renderLobbyChatPanelContent(false)}

      {/* Mobile Sticky Bottom Chat Panel / Toggle for Lobby */}
      <div className="mobile-lobby-chat-sticky-trigger" onClick={() => setIsLobbyChatOpen(true)}>
        <div className="chat-trigger-content">
          <span className="chat-trigger-icon">💬</span>
          <span className="chat-trigger-text">Lobi Sohbeti</span>
        </div>
      </div>

      {/* Mobile Lobby Chat Drawer / Bottom Sheet */}
      <div className={`mobile-chat-drawer-overlay ${isLobbyChatOpen ? 'open' : ''}`} onClick={(e) => {
        if (e.target.classList.contains('mobile-chat-drawer-overlay')) {
          setIsLobbyChatOpen(false);
        }
      }}>
        <div className="mobile-chat-drawer-content animate-slide-up">
          <div className="drawer-header">
            <span className="drawer-title">💬 Lobi Sohbeti</span>
            <button className="drawer-close-btn" onClick={() => setIsLobbyChatOpen(false)}>✕ Kapat</button>
          </div>
          {renderLobbyChatPanelContent(true)}
        </div>
      </div>

      {/* Unified Jail Card Overlay Modal (Lobby & Game) */}
      {selectedPropertyId === 'jail_card' && createPortal(
        <div 
          className="jail-card-overlay-modal animate-fade-in"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(5px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            boxSizing: 'border-box'
          }}
          onClick={() => setSelectedPropertyId(null)}
        >
          <div 
            className="deed-card chance-card-theme animate-scale-up" 
            style={{ 
              width: '240px', 
              border: '2.5px solid #10b981', 
              boxShadow: '0 0 30px rgba(16, 185, 129, 0.4)',
              borderRadius: '12px',
              overflow: 'hidden',
              background: '#0f172a',
              color: '#fff',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="deed-close-btn" 
              onClick={() => setSelectedPropertyId(null)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(0, 0, 0, 0.4)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#fff',
                fontSize: '0.9rem',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 110
              }}
            >
              ✕
            </button>
            <div 
              className="deed-color-header" 
              style={{ 
                background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                padding: '12px 0',
                color: '#fff',
                fontWeight: 800,
                fontSize: '0.85rem',
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}
            >
              Muafiyet Kartı
            </div>
            <div className="deed-body" style={{ padding: '20px', textAlign: 'center' }}>
              <h4 className="deed-name" style={{ fontSize: '1.15rem', margin: '0 0 12px 0', color: '#10b981', fontWeight: 800, fontFamily: 'Outfit, sans-serif' }}>Sürgün Çıkış Kartı</h4>
              <div style={{ fontSize: '3rem', margin: '14px 0' }}>🔑</div>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.5', margin: '0 0 16px 0', fontFamily: 'Outfit, sans-serif' }}>
                Bu kartı elinde bulunduran oyuncu, Çiğli'ye sürgüne gönderildiğinde hiçbir kefaret ödemeden doğrudan sürgünden çıkabilir.
              </p>
              <div className="deed-divider" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', margin: '14px 0' }}></div>
              
              {(() => {
                if (!gameData) return <div style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>Oyun henüz başlamadı.</div>;
                
                const me = gameData.players.find(p => p.playerId === myPlayerId);
                const isTurn = gameData.activePlayerId === myPlayerId;
                const inJail = me?.inJail;
                const canUse = inJail && isTurn && me?.jailFreeCards?.length > 0 && gameData.phase === 'WAITING_FOR_JAIL_DECISION';
                
                if (canUse) {
                  return (
                    <button 
                      className="gold-action-btn pulse-glow" 
                      style={{ width: '100%', padding: '10px 8px', fontSize: '0.75rem', background: '#10b981', borderColor: '#34d399', color: '#fff', fontWeight: 800, borderRadius: '8px', cursor: 'pointer' }}
                      onClick={() => {
                        sendCommand('USE_JAIL_CARD');
                        setSelectedPropertyId(null);
                      }}
                    >
                      KARTLA HAPİSTEN ÇIK
                    </button>
                  );
                }
                
                return (
                  <div style={{ fontSize: '0.72rem', color: '#64748b', fontStyle: 'italic' }}>
                    {inJail ? "Kullanmak için kendi sıranızı beklemelisiniz." : "Hapiste olmadığınız için şu an kullanılamaz."}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* SETTINGS MODAL */}
      {showSettingsModal && createPortal(
        <SettingsModal 
          onClose={() => setShowSettingsModal(false)}
          autoRoll={autoRoll}
          onToggleAutoRoll={handleToggleAutoRoll}
          autoBuy={autoBuy}
          onToggleAutoBuy={handleToggleAutoBuy}
          autoResolveCards={autoResolveCards}
          onToggleAutoResolveCards={handleToggleAutoResolveCards}
          showOthersCards={showOthersCards}
          onToggleShowOthersCards={handleToggleShowOthersCards}
          soundFxEnabled={soundFx}
          onToggleSoundFx={handleToggleSoundFx}
        />,
        document.body
      )}
    </div>
  );
}
